<template>
  <v-container fluid class="dashboard-container pa-0">
    <Onboard ref="onboarding" :refresh-fn="refresh" />

    <!-- Modern Header -->
    <div
      v-if="showHeader"
      class="header-section mb-6"
      style="margin: 12px 12px 0 12px; position: relative;"
    >
      <!-- Close Button -->
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-view-dashboard</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-1">Dashboard</h1>
          <p class="page-subtitle mb-0">{{ currentTime }}</p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <div class="live-indicator mr-2"></div>
            Live
          </v-chip>
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-chart-line</v-icon>
            Real-time
          </v-chip>
          <v-chip
            class="white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-clock-outline</v-icon>
            Today
          </v-chip>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0">
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-phone</v-icon>
            <span>Calls Tracking</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-account-group</v-icon
            >
            <span>Team Activity</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-chart-bar</v-icon>
            <span>Analytics</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-trending-up</v-icon
            >
            <span>Performance</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-clock-check-outline</v-icon
            >
            <span>Live Updates</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Main Content Grid -->
    <v-row class="ma-0">
      <!-- Stats Card (left column) -->
      <v-col cols="12" md="5" class="pa-2">
        <homeCard
          :compiled-data="{
            totalCallsToday: totalCallsToday,
          }"
        >
        </homeCard>
      </v-col>

      <!-- Charts Section (right column) -->
      <v-col cols="12" md="7" class="pa-2">
        <v-card
          v-if="totalCallsToday > 0"
          class="modern-card customBorderRadius"
          elevation="0"
        >
          <div class="card-header">
            <div class="d-flex align-center">
              <div class="icon-badge mr-3">
                <v-icon color="#FFFFFF">mdi-chart-box-outline</v-icon>
              </div>
              <div>
                <h3 class="card-title mb-0">Call Analytics</h3>
                <p class="card-subtitle mb-0">
                  Performance insights and trends
                </p>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="chart-wrapper">
                  <bar-chart
                    class="text-align-center justify-content-center mx-auto"
                    style="max-width: 100%; padding-top: 14px !important;"
                    :chart-data="datacollection"
                    :options="{
                      responsive: true,
                      legend: { position: 'bottom' },
                      scales: {
                        yAxes: [
                          {
                            gridLines: { display: false },
                            ticks: { beginAtZero: true },
                          },
                        ],
                        xAxes: [
                          {
                            type: 'time',
                            gridLines: { display: false },
                            time: { unit: 'month' },
                          },
                        ],
                      },
                    }"
                  ></bar-chart>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-wrapper">
                  <div
                    v-if="!loadChart"
                    style="text-align: center; padding: 50px"
                  >
                    <v-progress-circular
                      :width="3"
                      color="primary"
                      indeterminate
                    />
                  </div>
                  <div v-else align="center" class="mx-auto">
                    <doughnut-chart
                      v-if="loadChart"
                      :chart-data="datacollectionDoughnut"
                      class="text-align-center justify-content-center"
                      style="max-width: 100%; padding-top: 14px !important;"
                      :options="{
                        responsive: true,
                        legend: { position: 'bottom', display: labelShown },
                      }"
                    ></doughnut-chart>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card v-else class="modern-card customBorderRadius" elevation="0">
          <div class="card-header">
            <div class="d-flex align-center">
              <div class="icon-badge mr-3">
                <v-icon color="#FFFFFF">mdi-phone-off</v-icon>
              </div>
              <div>
                <h3 class="card-title mb-0">No Activity Today</h3>
                <p class="card-subtitle mb-0">
                  Start making calls to see analytics
                </p>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-6 text-center">
            <v-img contain height="100" :src="noCall" class="mb-4" />
            <h2 class="grey--text text--darken-1 mb-2">No Calls Today</h2>
            <p class="grey--text">
              Make your first call to see statistics and analytics here
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Team Activity Section -->
    <v-row class="ma-0 mt-4">
      <v-col cols="12" class="pa-2">
        <v-card class="modern-card customBorderRadius" elevation="0">
          <div class="card-header">
            <div class="d-flex align-center">
              <div class="icon-badge mr-3">
                <v-icon color="#FFFFFF">mdi-account-multiple</v-icon>
              </div>
              <div>
                <h3 class="card-title mb-0">Team Activity</h3>
                <p class="card-subtitle mb-0">
                  Live member performance and call history
                </p>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <!-- Search Section -->
            <v-row class="mb-4">
              <v-col cols="12" md="9">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  solo
                  flat
                  outlined
                  placeholder="Search member activity..."
                  dense
                  single-line
                  :hide-details="true"
                  class="modern-search"
                  @keyup.enter="getLatestActivity(4, 0, search)"
                  @click:clear="clearSearch"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  color="primary"
                  block
                  large
                  depressed
                  style="border-radius: 8px;"
                  @click="getLatestActivity(4, 0, search)"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>

            <!-- Loader -->
            <div v-if="preloader">
              <v-row class="pa-0 ma-0">
                <v-col v-for="i in 4" :key="i" cols="12" sm="6" md="3">
                  <v-skeleton-loader type="card-avatar"></v-skeleton-loader>
                </v-col>
              </v-row>
            </div>

            <!-- Member Activity Table -->
            <div v-if="visibleUsers.length > 0 && !preloader">
              <!-- Column Headers -->
              <div class="member-list-header d-none d-md-flex">
                <v-row align="center" class="ma-0" no-gutters>
                  <v-col md="3" class="mlh-cell pl-4">
                    <v-icon size="14" class="mr-1" color="grey">mdi-account-group</v-icon>
                    Team / Member
                  </v-col>
                  <v-col md="2" class="mlh-cell text-center">
                    <v-icon size="14" class="mr-1" color="grey">mdi-account-check</v-icon>
                    Status
                  </v-col>
                  <v-col md="2" class="mlh-cell text-center">
                    <v-icon size="14" class="mr-1" color="grey">mdi-timer-outline</v-icon>
                    Talk Time
                  </v-col>
                  <v-col md="1" class="mlh-cell text-center">
                    <v-icon size="14" class="mr-1" color="grey">mdi-chart-line</v-icon>
                    Productivity
                  </v-col>
                  <v-col md="2" class="mlh-cell text-center">
                    <v-icon size="14" class="mr-1" color="grey">mdi-calendar-check</v-icon>
                    Attendance
                  </v-col>
                  <v-col md="2" class="mlh-cell text-center">
                    <v-icon size="14" class="mr-1" color="grey">mdi-clock-outline</v-icon>
                    Last Active
                  </v-col>
                </v-row>
              </div>

              <div class="member-list">
                <div
                  v-for="(user, index) in visibleUsers"
                  :key="index"
                  class="member-item"
                >
                  <router-link
                    :to="{ path: `/performance?userid=${user._id}` }"
                    style="text-decoration:none"
                  >
                    <v-hover v-slot="{ hover }">
                      <div
                        :class="['member-card', { 'member-card-hover': hover }]"
                      >
                        <v-row align="center" class="ma-0" no-gutters>
                          <!-- Team / Member -->
                          <v-col
                            cols="12"
                            md="3"
                            class="d-flex align-center pa-3"
                          >
                            <v-avatar
                              :color="user.activeAgent ? 'primary' : 'grey'"
                              size="48"
                              class="member-avatar"
                              :style="
                                user.picture && user.activeAgent
                                  ? 'border: 3px solid #37cf37'
                                  : null
                              "
                            >
                              <v-img
                                v-if="user.picture"
                                :src="getImage(user.picture)"
                              />
                              <span
                                v-else
                                class="white--text font-weight-medium"
                              >
                                {{ user.name.substring(0, 2).toUpperCase() }}
                              </span>
                            </v-avatar>
                            <div class="ml-3" style="min-width:0;">
                              <h4 class="member-name">{{ user.name }}</h4>
                              <p class="member-role mb-0">{{ user.role }}</p>
                            </div>
                          </v-col>

                          <!-- Status / Active -->
                          <v-col cols="6" md="2" class="pa-3 text-center">
                            <span class="ta-mobile-label d-md-none">Status</span>
                            <v-chip
                              v-if="user.activeAgent"
                              x-small
                              color="success"
                              text-color="white"
                              class="status-chip"
                            >
                              <v-icon x-small left>mdi-circle</v-icon>
                              Active
                            </v-chip>
                            <v-chip
                              v-else
                              x-small
                              color="grey"
                              text-color="white"
                              class="status-chip"
                            >
                              <v-icon x-small left>mdi-circle-outline</v-icon>
                              Offline
                            </v-chip>
                          </v-col>

                          <!-- Talk Time -->
                          <v-col cols="6" md="2" class="pa-3 text-center">
                            <span class="ta-mobile-label d-md-none">Talk Time</span>
                            <div class="stat-item">
                              <div class="stat-value">
                                <v-icon size="16" color="primary" class="mr-1">mdi-timer-outline</v-icon>
                                {{
                                  user.last && user.last.callCounts > 0
                                    ? user.last.totalDuration
                                    : "0 min"
                                }}
                              </div>
                            </div>
                          </v-col>

                          <!-- Productivity (calls) -->
                          <v-col cols="6" md="1" class="pa-3 text-center">
                            <span class="ta-mobile-label d-md-none">Productivity</span>
                            <div class="stat-item">
                              <div class="stat-value">
                                <v-icon size="16" color="primary" class="mr-1">mdi-phone</v-icon>
                                {{
                                  user.last && user.last.callCounts > 0
                                    ? user.last.callCounts
                                    : 0
                                }}
                              </div>
                              <div class="stat-label">calls</div>
                            </div>
                          </v-col>

                          <!-- Attendance -->
                          <v-col cols="6" md="2" class="pa-3 text-center">
                            <span class="ta-mobile-label d-md-none">Attendance</span>
                            <template v-if="user.attendance === 'present' || user.attendance === true || (user.last && user.last.callCounts > 0)">
                              <v-chip x-small color="success" text-color="white" class="att-chip">
                                <v-icon x-small left>mdi-check-circle</v-icon>
                                Present
                              </v-chip>
                            </template>
                            <template v-else-if="user.attendance === 'absent' || user.attendance === false">
                              <v-chip x-small color="error" text-color="white" class="att-chip">
                                <v-icon x-small left>mdi-close-circle</v-icon>
                                Absent
                              </v-chip>
                            </template>
                            <template v-else>
                              <span class="grey--text">—</span>
                            </template>
                          </v-col>

                          <!-- Last Active -->
                          <v-col cols="12" md="2" class="pa-3 text-center">
                            <span class="ta-mobile-label d-md-none">Last Active</span>
                            <div class="last-call-time">
                              <template v-if="user.last != undefined">
                                {{
                                  $moment(
                                    user.last.calledOn,
                                    "YYYY-MM-DD h:mm A",
                                  ).fromNow()
                                }}
                              </template>
                              <template v-else>
                                <span class="grey--text">No history</span>
                              </template>
                            </div>
                          </v-col>
                        </v-row>
                      </div>
                    </v-hover>
                  </router-link>
                </div>
              </div>

              <!-- Pagination -->
              <v-divider class="my-4"></v-divider>
              <div class="pagination-wrapper">
                <div class="d-flex align-center">
                  <span class="grey--text mr-3">Rows per page:</span>
                  <v-select
                    v-model="records"
                    :items="recordsPerPage"
                    dense
                    outlined
                    hide-details
                    style="max-width: 80px"
                    @change="pagination"
                  ></v-select>
                </div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center">
                  <span class="grey--text mr-3">
                    {{ `${page}/${pageCount} of ${totalRecords}` }}
                  </span>
                  <v-btn
                    :disabled="page == 1"
                    color="primary"
                    icon
                    @click="prevSet"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    :disabled="page >= pageCount"
                    color="primary"
                    icon
                    @click="nextSet"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- No Members -->
            <div
              v-if="visibleUsers.length <= 0 && !preloader"
              class="text-center py-8"
            >
              <v-img :src="noAgent" width="200" class="mx-auto mb-4" />
              <h3 class="grey--text text--darken-1">No Members Available</h3>
              <p class="grey--text">
                Add team members to see their activity here
              </p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialogs -->
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Please wait
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="contactDialog" width="800">
      <v-card color="primary" dark>
        <v-carousel
          :show-arrows="false"
          :horizontal="true"
          hide-delimiter-background
        >
          <v-carousel-item>
            <v-card
              v-touch="{
                left: () => swipe('Left'),
                right: () => swipe('Right'),
              }"
              light
            >
              <v-card-text>
                <v-flex xs12 sm12 md12>
                  <v-img src="../assets/clickList.png"></v-img>
                </v-flex>
              </v-card-text>
            </v-card>
          </v-carousel-item>

          <v-carousel-item>
            <v-card
              v-touch="{
                left: () => swipe('Left'),
                right: () => swipe('Right'),
              }"
              light
            >
              <v-card-text>
                <v-flex xs12 sm12 md12>
                  <v-img src="../assets/addContact.png"></v-img>
                </v-flex>
              </v-card-text>
            </v-card>
          </v-carousel-item>

          <v-carousel-item>
            <v-card
              v-touch="{
                left: () => swipe('Left'),
                right: () => swipe('Right'),
              }"
              light
            >
              <v-card-text>
                <v-flex xs12 sm12 md12>
                  <v-img src="../assets/AssignMembersImg.png"></v-img>
                </v-flex>
              </v-card-text>
            </v-card>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>

    <v-dialog v-model="callDialog" width="800">
      <v-card color="primary" dark>
        <v-carousel
          :show-arrows="false"
          :horizontal="true"
          hide-delimiter-background
        >
          <v-carousel-item>
            <v-card
              v-touch="{
                left: () => swipe('Left'),
                right: () => swipe('Right'),
              }"
              light
            >
              <v-card-text>
                <v-flex xs12 sm12 md12>
                  <v-img src="../assets/callNumber.png"></v-img>
                </v-flex>
              </v-card-text>
            </v-card>
          </v-carousel-item>

          <v-carousel-item>
            <v-card
              v-touch="{
                left: () => swipe('Left'),
                right: () => swipe('Right'),
              }"
              light
            >
              <v-card-text>
                <v-flex xs12 sm12 md12>
                  <v-img src="../assets/setDispo.png"></v-img>
                </v-flex>
              </v-card-text>
            </v-card>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>

    <v-dialog v-model="memberDialog" width="800">
      <v-card color="primary" dark>
        <v-carousel
          :show-arrows="false"
          :horizontal="true"
          hide-delimiter-background
        >
          <v-carousel-item>
            <v-card
              v-touch="{
                left: () => swipe('Left'),
                right: () => swipe('Right'),
              }"
              light
            >
              <v-card-text>
                <v-flex xs12 sm12 md12>
                  <v-img src="../assets/clickMembers.png"></v-img>
                </v-flex>
              </v-card-text>
            </v-card>
          </v-carousel-item>

          <v-carousel-item>
            <v-card
              v-touch="{
                left: () => swipe('Left'),
                right: () => swipe('Right'),
              }"
              light
            >
              <v-card-text>
                <v-flex xs12 sm12 md12>
                  <v-img src="../assets/addMembers.png"></v-img>
                </v-flex>
              </v-card-text>
            </v-card>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>

    <welcome-popup
      ref="welcomePopupOpen"
      @refreshHome="refresh"
    ></welcome-popup>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment-timezone";
// import PieChart from "../js/pie.js";
import homeCard from "../components/home-card";
import welcomePopup from "../components/welcomePopup";
import Onboard from "../components/onboard.vue";
//import LineChart from "../js/linechart.js";

import BarChart from "../js/bar.js";
import DoughnutChart from "../js/doughnut.js";

export default {
  components: {
    // LineChart,
    BarChart,
    DoughnutChart,
    // PieChart,
    homeCard,
    welcomePopup,
    Onboard,
  },
  data: () => {
    return {
      showHeader: false, // Hidden globally - set to true to show again
      currentTime: "",
      visibleUsers: [],
      preloader: false,
      callDialog: false,
      memberDialog: false,
      contactDialog: false,
      contactCardActive: false,
      callCardActive: false,
      memberCardActive: false,
      e6: 1,
      allUsers: [],
      callsData: [],
      showWizard: true,
      loadChart: true,
      search: "",
      datacollection: {},
      datacollectionDoughnut: {},
      callDetails: [],
      lastActivity: [],
      totalCallsToday: 0,
      labelShown: true,

      headers: [
        { text: "Name", align: "left", sortable: true, value: "name" },
        { text: "Role", align: "left", sortable: true, value: "role" },
        {
          text: "Last Call",
          align: "left",
          sortable: true,
          value: "last.formatedTime",
        },
        {
          text: "Last Call Name",
          align: "left",
          sortable: true,
          value: "last.name",
        },
        {
          text: "Last Call Status",
          align: "left",
          sortable: true,
          value: "last.dispo",
        },
        {
          text: "Call Counts",
          align: "left",
          sortable: true,
          value: "last.callCounts",
        },
        {
          text: "Call Summary",
          align: "left",
          sortable: true,
          value: "last.totalDuration",
        },
        {
          text: "Last Seen",
          align: "left",
          sortable: true,
          value: "last.createdOn",
        },
      ],
      // pagination structure
      page: 1,
      pageCount: 0,
      totalRecords: 0,
      records: 4,
      recordsPerPage: [4, 8, 16, 32, 64],
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "user", "dispoList", "companyDetails"]),
    selectedTeam() {
      return this.$store.getters.selectedTeam;
    },
    noAgent() {
      return require("../assets/noAgent.png");
    },
    teams() {
      return this.$store.getters.teams;
    },
    loading() {
      return this.$store.state.loading;
    },
    noData() {
      return require("../assets/no-page-found.png");
    },
    stepImg1() {
      return require("../assets/step1.png");
    },
    stepImg2() {
      return require("../assets/step2.png");
    },
    stepImg3() {
      return require("../assets/step3.png");
    },
    stepImg4() {
      return require("../assets/step4.png");
    },
    noCall() {
      return require("../assets/nocall.png");
    },
    paginate() {
      return this.$store.getters.paginate;
    },
  },
  watch: {
    selectedTeam() {
      this.getLatestActivity();
      this.fillData();
    },
  },
  beforeDestroy() {
    clearInterval(this.timeInterval); // Clear the interval when the component is destroyed
  },
  async created() {
    // Header is hidden globally - uncomment below line to restore user preference
    // this.showHeader = localStorage.getItem("dashboardHeaderDismissed") !== "true";
    this.showHeader = false;
    await this.refreshData();
  },
  mounted() {
    this.updateTime(); // Set the initial time
    this.timeInterval = setInterval(this.updateTime, 1000);
    // welcome screen
    if (typeof this.companyDetails == "undefined") {
      return;
    }

    if (this.companyDetails != null && this.companyDetails.ftsDone === false) {
      // open dialog for welcome pop up
      this.$refs.welcomePopupOpen.open();
      // this.$refs.onboarding.openDialog();
    }
    // TODO
    // if (this.companyDetails != null && this.companyDetails.useOfGodial && this.companyDetails.ftsDone === false) {
    //   this.$refs.welcomePopupOpen.open();
    // }
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("dashboardHeaderDismissed", "true");
    },
    updateTime() {
      this.currentTime = this.$moment().format("Do MMMM YYYY, h:mm:ss A");
    },
    clearSearch() {
      this.search = "";
      this.getLatestActivity();
    },
    async refresh() {
      await this.refreshData();
    },
    async refreshData() {
      // Paginate

      var routeName = this.$router.currentRoute.name
        .toString()
        .trim()
        .replace(/ /g, "")
        .toLowerCase();
      if (this.paginate[routeName]) {
        this.page = this.paginate[routeName].page;
        this.pageCount = this.paginate[routeName].pageCount;
        this.totalRecords = this.paginate[routeName].totalRecords;
        this.records = this.paginate[routeName].records;

        if (this.totalRecords < this.records) {
          this.records = this.totalRecords;
        }
      }

      console.log("this.page", this.page);
      console.log("this.pageCount", this.pageCount);
      console.log("this.totalRecords", this.totalRecords);
      console.log("this.records", this.records);

      await this.getLatestActivity();

      this.fillData();
      // this.memberInCompany();
      // this.contactInCompany();
      // this.callInCompany();
      this.getCompanyParams();
    },
    addContactImgDialog() {
      this.contactDialog = true;
    },
    addMemberImgDialog() {
      this.memberDialog = true;
    },
    addCallImgDialog() {
      this.callDialog = true;
    },
    async getCompanyParams() {
      this.$http
        .post(this.ENDPOINT + "/companies/check-company-params", {
          companyId: this.$store.getters.user.companyId,
        })
        .then((response) => {
          this.callCardActive = false;
          this.contactCardActive = false;
          this.memberCardActive = false;
          if (response.body.accounts < 2) {
            this.memberCardActive = true;
          }
          if (response.body.contacts < 1) {
            this.contactCardActive = true;
          }
          if (response.body.logs < 1) {
            this.callCardActive = true;
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    },

    async doneWizard() {
      this.loading = true;
      await this.$http.patch(
        this.ENDPOINT + "/companies/" + this.companyDetails.id,
        {
          setupDone: true,
        },
      );

      this.$store.dispatch("getCompanyDetails", {
        companyId: this.companyDetails.id,
      });

      this.loading = false;
    },

    fillData() {
      var self = this;
      // Teams sections
      var teamIds = this.getTeamIds();

      self.callDetails = [];
      self.loadChart = false;

      var startDate = moment()
        .subtract(6, "d")
        .format("YYYY-MM-DD");
      var endDate = moment().format("YYYY-MM-DD");

      self.$http
        .post(self.ENDPOINT + "/logs/v2/countbyday", {
          range: [startDate, endDate],
          teamsId: teamIds,
        })
        .then((res) => {
          self.totalCallsToday = 0;
          try {
            if (res.body.length > 0) {
              var days = {};
              var pie = {};
              _.each(res.body, (r) => {
                let k = moment(r._id.day).format();

                if (typeof days[k] == "undefined") days[k] = 0;
                days[k] = days[k] + r.calls;

                if (typeof pie[k] == "undefined") pie[k] = [];
                pie[k].push(r.status);
              });

              let data = _.map(_.keys(days), (key) => {
                return { x: moment(key).toDate(), y: days[key] };
              });

              data = _.orderBy(data, (d) => {
                return d.x;
              });

              // populate data for line chart
              self.datacollection = {
                datasets: [
                  {
                    label: "Last 7 Days Performance",
                    backgroundColor: "#4cd964",
                    data: data,
                  },
                ],
              };

              let current = moment().format("YYYY-MM-DD");
              var today = [];
              for (var x in pie) {
                if (
                  moment(current).isSame(moment(x).format("YYYY-MM-DD"), "day")
                ) {
                  today = pie[x];
                  break;
                }
              }

              pie = {};
              _.each(today, (t) => {
                _.mapKeys(t, (value, key) => {
                  if (typeof pie[key] == "undefined") pie[key] = 0;
                  pie[key] = pie[key] + value;
                });
              });

              var colors = [];

              this.labelShown = false; // mention at top
              let result = Object.keys(pie).map((key) => ({
                _id: key,
                count: Number(pie[key]),
              }));
              pie = Object.assign(result);

              pie = _.orderBy(pie, ["count"], ["desc"]);
              if (pie.length > 10) {
                var pieNew = pie.slice(0, 10);
                var pieOther = {
                  _id: "Others",
                  count: 0,
                };
                for (let i = 10; i < pie.length; i++) {
                  pieOther.count += pie[i].count;
                }
                pie = pieNew.concat(pieOther);
              }

              pie.forEach((val) => {
                val = _.find(self.dispoList, (d) => {
                  return d.title == val._id;
                });
                if (val == undefined) {
                  colors.push("#d9d9d9");
                } else {
                  colors.push(val.hex);
                }
              });

              self.datacollectionDoughnut = {
                datasets: [
                  {
                    backgroundColor: colors,
                    data: _.map(pie, "count"), // [..._.map(pie)],
                    borderWidth: 0,
                  },
                ],
                labels: _.map(pie, "_id"), //Object.keys(pie),
              };

              // console.log(
              //   "self.dataCollection pie",
              //   JSON.stringify(self.datacollectionDoughnut)
              // );

              self.$nextTick(() => {
                self.labelShown = true;
                self.loadChart = true;
                var current = moment();
                var today = _.find(data, (d) => {
                  return current.isSame(moment(d.x), "day");
                });
                self.totalCallsToday =
                  typeof today != "undefined" ? today.y : 0;
              });
            }
          } catch (e) {
            console.log("Error", e);
          }
        })
        .catch((e) => {
          console.log("error", e);
        });
    },
    getRandomInt() {
      return Math.floor(Math.random() * (50 - 5 + 1)) + 5;
    },

    /** PAGINATION */
    //next and prev buttons
    nextSet() {
      this.page++;
      this.pagination();
    },
    prevSet() {
      this.page--;
      this.pagination();
    },
    async pagination() {
      try {
        this.pageCount = Math.ceil(this.totalRecords / this.records);
        if (this.page > this.pageCount) {
          this.page = 1; //set default
        }
        await this.getLatestActivity(
          this.records,
          this.records * this.page - this.records,
        );

        this.paginate[
          this.$router.currentRoute.name
            .toString()
            .trim()
            .replace(/ /g, "")
            .toLowerCase()
        ] = {
          page: this.page,
          pageCount: this.pageCount,
          records: this.records,
          totalRecords: this.totalRecords,
        };
        this.$store.commit("setPagination", this.paginate);
        return;
      } catch (e) {
        console.log(e);
        this.preloader = false;
      }
    },
    // fetch the total number of data avail
    async fetchCount() {
      this.preloader = true;

      // Teams sections
      var teamIds = this.getTeamIds();

      // var where = {
      //   companyId: this.$store.getters.user.companyId,
      //   teamsId: {
      //     inq: teamIds,
      //   },
      // };
      // if (this.search != "" && this.search != null) {
      //   // this.search = new RegExp(this.search.toLowerCase().trim(), "i");
      //   where.or = [
      //     {
      //       username: {
      //         like: this.search,
      //         options: "i",
      //       },
      //     },
      //     {
      //       name: {
      //         like: this.search,
      //         options: "i",
      //       },
      //     },
      //     {
      //       email: {
      //         like: this.search,
      //         options: "i",
      //       },
      //     },
      //   ];
      // }

      var response = await this.$http.post(
        `${this.$store.state.ENDPOINT}/accounts/fetchCount-v2`,
        {
          teams: teamIds,
          search: this.search,
        },
      );

      this.totalRecords = response.body;
      this.pageCount = Math.ceil(this.totalRecords / this.records);
      this.preloader = false;

      // this.$http
      //   .get(
      //     `${
      //       this.$store.state.ENDPOINT
      //     }/accounts/count?where=${encodeURIComponent(JSON.stringify(where))}`
      //   )
      //   .then((res) => {
      //     this.totalRecords = res.body.count;
      //     this.pageCount = Math.ceil(this.totalRecords / this.records);
      //     this.preloader = false;
      //   })
      //   .catch((e) => {
      //     console.log(e);
      //     this.preloader = false;
      //   });
    },
    /** recieve teams array of ids */
    getTeamIds() {
      var teams = [];
      if (this.selectedTeam == "allTeams") {
        teams = _.map(this.teams, "id");
      } else {
        teams.push(this.selectedTeam);
      }
      teams = _.without(teams, "allTeams");
      return teams;
    },
    // get the users and also used for pagination
    getLatestActivity(limit, skip, search) {
      var self = this;
      this.preloader = true;

      if (limit == undefined || skip == undefined) {
        limit = this.records;
        skip = 0;
      }

      if (search == undefined) {
        search = "";
      }

      // Teams sections
      var teamIds = this.getTeamIds();

      self.$http
        .post(`${self.ENDPOINT}/accounts/lastActivity`, {
          teamsId: teamIds,
          limit: limit,
          skip: skip,
          search: search,
        })
        .then(async (response) => {
          self.allUsers = JSON.parse(JSON.stringify(response.body));

          for (var x in self.allUsers) {
            if (self.allUsers[x].last != undefined) {
              if (
                moment().diff(
                  moment(self.allUsers[x].last.calledOn),
                  "Minutes",
                ) < 5
              ) {
                self.allUsers[x].activeAgent = true;
              } else {
                self.allUsers[x].activeAgent = false;
              }
            } else {
              self.allUsers[x].activeAgent = false;
            }
          }

          // self.visibleUsers = self.allUsers;

          self.visibleUsers = self.allUsers.sort((a, b) => {
            return b.activeAgent - a.activeAgent;
          });

          var res = [];
          res = _.without(
            _.map(self.allUsers, (r) => {
              if (r.last != undefined) {
                r.last.formatedTime = moment(r.last.calledOn).format(
                  "YYYY-MM-DD hh:mm A",
                );
                r.last.calledOn = moment(r.last.calledOn).format(
                  "YYYY-MM-DD hh:mm A",
                );

                r.last.totalDuration = r.last.totalDuration / 1000;
                r.last.totalDuration = `${Number(
                  r.last.totalDuration / 60,
                ).toFixed(1)} mins`;
              }

              return r;
            }),
            undefined,
          );

          self.allUsers = res;

          self.preloader = false;
          self.fetchCount();
        })
        .catch((e) => {
          //error
          self.preloader = false;
          // self.allUsers = [];
          // self.visibleUsers = [];
          self.$swal({
            type: "error",
            text: "Error while getting all the member details",
          });
        });
    },
    getImage(src) {
      return `${this.$store.state.ENDPOINT}/storages/get/${src}?access_token=${this.$store.getters.user.token}`;
    },
  },
};
</script>

<style scoped>
/* Dashboard Container */
.dashboard-container {
  background: var(--v-background-base, #f5f7fa);
  min-height: 100vh;
  padding: 0 !important;
  max-width: 100% !important;
}

.theme--dark .dashboard-container {
  background: #121212 !important;
}

.theme--dark .header-section {
  margin-top: 16px;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  padding: 32px;
  border-radius: 12px;
  margin: 12px 12px 24px 12px;
  box-shadow: 0 4px 12px rgba(55, 207, 55, 0.2);
  position: relative;
}

.header-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.2) !important;
  transition: all 0.3s ease;
}

.header-close-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
}

.header-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.feature-pills {
  margin-top: 24px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.live-indicator {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background: #ffffff;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
}

/* Modern Cards */
.modern-card {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0;
  background: var(--v-background-base, white) !important;
  transition: all 0.3s ease;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.modern-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.card-header {
  padding: 20px 24px;
  background: var(--v-background-base, white);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.theme--dark .card-header {
  background: #1e1e1e !important;
}

.icon-badge {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.3);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.card-subtitle {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.theme--dark .card-title {
  color: #ffffff !important;
}

.theme--dark .card-subtitle {
  color: #b0b0b0 !important;
}

/* Charts */
.chart-wrapper {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.theme--dark .chart-wrapper {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

/* Search */
.modern-search {
  border-radius: 8px !important;
}

.modern-search >>> .v-input__slot {
  border-radius: 8px !important;
}

/* Member List */
.member-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Column headers above member list */
.member-list-header {
  padding: 10px 4px;
  margin-bottom: 8px;
  background: #f6faf6;
  border: 1px solid #e6efe6;
  border-radius: 10px;
}

.theme--dark .member-list-header {
  background: #232a23 !important;
  border-color: #2f3a30 !important;
}

.mlh-cell {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #4a4a4a;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mlh-cell.pl-4 {
  justify-content: flex-start;
}

.theme--dark .mlh-cell {
  color: #cfcfcf;
}

.ta-mobile-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 4px;
  letter-spacing: 0.4px;
}

.status-chip,
.att-chip {
  font-weight: 600;
}

.member-item {
  display: block;
}

.member-card {
  background: var(--v-background-base, white);
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 0;
  transition: all 0.3s ease;
}

.theme--dark .member-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.member-card:hover {
  border-color: #37cf37;
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.15);
  transform: translateY(-2px);
}

.member-card-hover {
  border-color: #37cf37;
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.15);
  transform: translateY(-2px);
}

.member-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.member-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 4px 0;
  text-transform: uppercase;
}

.theme--dark .member-name {
  color: #ffffff !important;
}

.member-role {
  font-size: 13px;
  color: #6c757d;
  margin: 0 0 6px 0;
}

.theme--dark .member-role {
  color: #b0b0b0 !important;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 4px 0 2px 0;
}

.stat-label {
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.last-call-info {
  font-size: 13px;
}

.last-call-label {
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 4px;
}

.last-call-time {
  color: #2c3e50;
  font-weight: 500;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  align-items: center;
  padding: 16px 0;
}

/* Legacy Styles (kept for compatibility) */
.customPadding {
  padding-top: 0.5%;
}

.customColorForAvatarText {
  color: white !important;
  font-size: 24px !important;
}

.active-dot {
  height: 14px;
  width: 14px;
  background-color: #44ff00;
  border-radius: 50%;
  display: inherit;
  position: absolute;
  margin-top: 44px;
  margin-left: 40px;
}

.dotted-gradient {
  background-image: linear-gradient(
    to right,
    #313131 40%,
    rgba(255, 255, 255, 0) 40%
  );
  background-position: bottom;
  background-size: 6px 2px;
  background-repeat: repeat-x;
  text-transform: inherit;
  padding-bottom: 2px;
  opacity: 0.7;
}

.live2 {
  height: 10px;
  width: 10px;
  border-radius: 50%;
  background: rgb(95, 255, 47);
  margin: 12px 10px;
  animation-name: pulse2;
  animation-duration: 500ms;
  animation-iteration-count: infinite;
  animation-direction: alternate;
}

@keyframes pulse2 {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}
</style>
